'use client';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, LoadingSpinner } from '@crabs-ai/ds-react';
import {
  usePostAiSceneGenerateApi,
  usePostAiSceneModifyApi,
  usePostAiCodeGenerateApi,
} from '@/shared/apis/editor/post-ai-scene-generate';
import type {
  AiItemCurrentState,
  AiItemAction,
  AiSoundEffectAction,
  AiTransitionCurrentState,
  AiTransitionChange,
} from '@/shared/apis/editor/post-ai-scene-generate';
import { getSoundEffectById } from '@/features/remotion-editor/scripts/sound-effect-data';
import {
  useWriteContext,
  useAllItems,
  useTransitions,
  useTracks,
} from '@/features/remotion-editor/editor/utils/use-context';
import type { EditorStarterItem } from '@/features/remotion-editor/editor/items/item-type';
import type { CodeItem } from '@/features/remotion-editor/editor/items/code/code-item-type';
import { preloadBabel } from '@/shared/lib/remotion';
import { addAiScene } from '@/features/remotion-editor/editor/state/actions/add-ai-scene';
import { changeItems } from '@/features/remotion-editor/editor/state/actions/change-items';
import { generateRandomId } from '@/features/remotion-editor/editor/utils/generate-random-id';
import { getZoneForItemType } from '@/features/remotion-editor/editor/utils/zone-utils';
import type { AnimationType } from '@/features/remotion-editor/editor/items/animation/animation-item-type';
import {
  createTransitionItem,
  DEFAULT_TRANSITION_DURATION_IN_FRAMES,
} from '@/features/remotion-editor/editor/items/transition/transition-item-type';
import type {
  TransitionType,
  TransitionDirection,
} from '@/features/remotion-editor/editor/editor-property-panel/ui/contents/transition/transition-drag-data';
import { v4 as uuidv4 } from 'uuid';
import { useTranslations } from 'next-intl';

interface AiSceneContentProps {
  projectId: number;
}

type TabMode = 'generate' | 'modify' | 'code';

function AiSceneContent({ projectId }: AiSceneContentProps) {
  const t = useTranslations();
  const { setState } = useWriteContext();
  const { items: allItems } = useAllItems();
  const { transitions: allTransitions } = useTransitions();
  const { tracks } = useTracks();
  const { mutate: generateScene, isPending: isGenerating } =
    usePostAiSceneGenerateApi();
  const { mutate: modifyScene, isPending: isModifying } =
    usePostAiSceneModifyApi();
  const { mutate: generateCode, isPending: isGeneratingCode } =
    usePostAiCodeGenerateApi();

  const [activeTab, setActiveTab] = useState<TabMode>(() => {
    if (typeof window === 'undefined') return 'code';
    return (localStorage.getItem('ai-scene-tab') as TabMode) || 'code';
  });
  const [prompt, setPrompt] = useState(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem('ai-scene-prompt') || '';
  });
  const [referenceImageUrl, setReferenceImageUrl] = useState(() => {
    if (typeof window === 'undefined') return '';
    return (
      localStorage.getItem('ai-scene-image-url') ||
      'https://crabs-projects-dev.s3.ap-northeast-2.amazonaws.com/test/final_output_01.png'
    );
  });
  const [chatResponse, setChatResponse] = useState('');
  const [error, setError] = useState('');

  // localStorage 동기화
  useEffect(() => {
    localStorage.setItem('ai-scene-tab', activeTab);
  }, [activeTab]);
  useEffect(() => {
    localStorage.setItem('ai-scene-prompt', prompt);
  }, [prompt]);
  useEffect(() => {
    localStorage.setItem('ai-scene-image-url', referenceImageUrl);
  }, [referenceImageUrl]);

  const isPending = isGenerating || isModifying || isGeneratingCode;

  // 현재 타임라인의 전체 아이템을 AI 전달용으로 직렬화
  const allItemsForAi = useMemo((): AiItemCurrentState[] => {
    return (Object.values(allItems) as EditorStarterItem[]).map((item) => {
      const base: AiItemCurrentState = {
        id: item.id,
        type: item.type,
        fromFrame: item.from,
        durationInFrames: item.durationInFrames,
        top: item.top,
        left: item.left,
        width: item.width,
        height: item.height,
        opacity: item.opacity,
        animationType:
          'animations' in item ? (item.animations?.[0]?.type ?? null) : null,
        fadeInSeconds:
          'fadeInDurationInSeconds' in item
            ? (item.fadeInDurationInSeconds as number)
            : undefined,
        fadeOutSeconds:
          'fadeOutDurationInSeconds' in item
            ? (item.fadeOutDurationInSeconds as number)
            : undefined,
      };

      switch (item.type) {
        case 'text':
          return {
            ...base,
            text: item.text,
            fontSize: item.fontSize,
            color: item.color,
            align: item.align,
            fontFamily: item.fontFamily,
          };
        case 'solid':
          return { ...base, color: item.color };
        case 'video':
          return { ...base, playbackRate: item.playbackRate };
        case 'captions':
          return {
            ...base,
            fontSize: item.fontSize,
            color: item.color,
            align: item.align,
            fontFamily: item.fontFamily,
            highlightColor: item.highlightColor,
          };
        case 'audio':
          return {
            ...base,
            playbackRate: item.playbackRate,
            decibelAdjustment: item.decibelAdjustment,
          };
        case 'gif':
          return { ...base, playbackRate: item.playbackRate };
        case 'blur':
          return { ...base, blurAmount: item.blurAmount };
        default:
          return base;
      }
    });
  }, [allItems]);

  // 현재 트랜지션을 AI 전달용으로 직렬화
  const transitionsForAi = useMemo((): AiTransitionCurrentState[] => {
    return Object.values(allTransitions).map((tr) => ({
      id: tr.id,
      transitionType: tr.transitionType,
      direction: tr.direction ?? null,
      durationInFrames: tr.durationInFrames,
      sourceItemId: tr.sourceItemId,
      targetItemId: tr.targetItemId,
      trackId: tr.trackId,
    }));
  }, [allTransitions]);

  const itemCount = allItemsForAi.length;

  // ============================================================
  // Generate handler (기존)
  // ============================================================
  const handleGenerate = useCallback(() => {
    if (!prompt.trim() || isPending) return;
    setError('');
    setChatResponse('');

    generateScene(
      {
        projectId,
        prompt: prompt.trim(),
        language: 'ko',
        maxDurationSeconds: 15,
        aspectRatio: '9:16',
      },
      {
        onSuccess: (response) => {
          const sceneResult = response.data?.sceneResult;
          const message = response.data?.chatResponse;
          if (!sceneResult || !sceneResult.segments?.length) {
            setError('씬 생성 결과가 없습니다. 다른 프롬프트로 시도해주세요.');
            return;
          }
          setState({
            update: (s) => addAiScene({ state: s, sceneResult }),
            commitToUndoStack: true,
          });
          if (message) setChatResponse(message);
        },
        onError: (err) => {
          setError(
            err.response?.data?.message ||
              '씬 생성에 실패했습니다. 잠시 후 다시 시도해주세요.',
          );
        },
      },
    );
  }, [prompt, isPending, projectId, generateScene, setState]);

  // ============================================================
  // Modify handler (신규)
  // ============================================================
  const handleModify = useCallback(() => {
    if (!prompt.trim() || isPending || itemCount === 0) return;
    setError('');
    setChatResponse('');

    modifyScene(
      {
        projectId,
        prompt: prompt.trim(),
        items: allItemsForAi,
        transitions: transitionsForAi,
        language: 'ko',
      },
      {
        onSuccess: (response) => {
          const modifyResult = response.data?.modifyResult;
          const message = response.data?.chatResponse;

          const hasItemChanges = (modifyResult?.items?.length ?? 0) > 0;
          const hasTransitionChanges =
            (modifyResult?.transitions?.length ?? 0) > 0;
          const hasSoundEffectChanges =
            (modifyResult?.soundEffects?.length ?? 0) > 0;

          // DEBUG: AI 응답 확인
          console.log(
            '[AI Asst] modifyResult:',
            JSON.stringify(modifyResult, null, 2),
          );

          if (
            !hasItemChanges &&
            !hasTransitionChanges &&
            !hasSoundEffectChanges
          ) {
            setChatResponse(
              message ||
                '수정할 내용을 찾지 못했습니다. 다른 프롬프트를 시도해주세요.',
            );
            return;
          }

          setState({
            update: (s) => {
              let updated = s;

              // 1. 아이템 액션 적용 (delete -> update -> create 순서)
              if (hasItemChanges && modifyResult?.items) {
                updated = applyItemActions(updated, modifyResult.items);
              }

              // 2. 트랜지션 변경 적용
              if (hasTransitionChanges && modifyResult?.transitions) {
                updated = applyTransitionChanges(
                  updated,
                  modifyResult.transitions,
                  tracks,
                );
              }

              // 3. 효과음 변경 적용
              if (hasSoundEffectChanges && modifyResult?.soundEffects) {
                updated = applySoundEffectChanges(
                  updated,
                  modifyResult.soundEffects,
                );
              }

              return updated;
            },
            commitToUndoStack: true,
          });

          if (message) setChatResponse(message);
        },
        onError: (err) => {
          setError(
            err.response?.data?.message ||
              '수정에 실패했습니다. 잠시 후 다시 시도해주세요.',
          );
        },
      },
    );
  }, [
    prompt,
    isPending,
    itemCount,
    projectId,
    allItemsForAi,
    transitionsForAi,
    tracks,
    modifyScene,
    setState,
  ]);

  // ============================================================
  // Code Generate handler
  // ============================================================
  const handleCodeGenerate = useCallback(() => {
    if (!prompt.trim() || isPending) return;
    setError('');
    setChatResponse('');

    generateCode(
      {
        projectId,
        prompt: prompt.trim(),
        compositionWidth: 1080,
        compositionHeight: 1920,
        fps: 30,
        language: 'ko',
        maxDurationSeconds: 30,
        referenceImageUrl: referenceImageUrl.trim() || null,
      },
      {
        onSuccess: (response) => {
          const {
            code,
            title,
            durationInFrames,
            chatResponse: msg,
          } = response.data ?? {};
          if (!code) {
            setError(
              '코드 생성 결과가 없습니다. 다른 프롬프트로 시도해주세요.',
            );
            return;
          }

          // CodeItem 생성 후 EditorState에 삽입
          const codeItemId = uuidv4();
          const trackId = uuidv4();
          const codeItem: CodeItem = {
            id: codeItemId,
            type: 'code',
            from: 0,
            durationInFrames: durationInFrames || 150,
            top: 0,
            left: 0,
            width: 1080,
            height: 1920,
            opacity: 1,
            rotation: 0,
            isDraggingInTimeline: false,
            code,
            title: title || 'Generated Code',
            fadeInDurationInSeconds: 0,
            fadeOutDurationInSeconds: 0,
          };

          setState({
            update: (s) => ({
              ...s,
              undoableState: {
                ...s.undoableState,
                items: {
                  ...s.undoableState.items,
                  [codeItemId]: codeItem,
                },
                tracks: [
                  {
                    id: trackId,
                    items: [codeItemId],
                    hidden: false,
                    muted: false,
                    locked: false,
                    magnetic: false,
                    zone: getZoneForItemType('code'),
                  },
                  ...s.undoableState.tracks,
                ],
              },
            }),
            commitToUndoStack: true,
          });

          if (msg) setChatResponse(msg);
        },
        onError: (err) => {
          setError(
            err.response?.data?.message ||
              '코드 생성에 실패했습니다. 잠시 후 다시 시도해주세요.',
          );
        },
      },
    );
  }, [prompt, isPending, projectId, generateCode, setState]);

  // ============================================================
  // Key handler
  // ============================================================
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (activeTab === 'generate') handleGenerate();
        else if (activeTab === 'modify') handleModify();
        else handleCodeGenerate();
      }
    },
    [activeTab, handleGenerate, handleModify, handleCodeGenerate],
  );

  const isActionDisabled =
    !prompt.trim() || isPending || (activeTab === 'modify' && itemCount === 0);

  const getActionHandler = () => {
    if (activeTab === 'generate') return handleGenerate;
    if (activeTab === 'modify') return handleModify;
    return handleCodeGenerate;
  };

  const getButtonLabel = () => {
    if (isPending) {
      if (activeTab === 'generate') return t('Editor.ai-scene.generating');
      if (activeTab === 'modify') return t('Editor.ai-scene.modifying');
      return '코드 생성 중...';
    }
    if (activeTab === 'generate') return t('Editor.ai-scene.generate');
    if (activeTab === 'modify') return t('Editor.ai-scene.modify-button');
    return '코드 생성';
  };

  const getPlaceholder = () => {
    if (activeTab === 'generate') return t('Editor.ai-scene.placeholder');
    if (activeTab === 'modify') return t('Editor.ai-scene.modify-placeholder');
    return '예: Hello World 텍스트가 페이드인 되는 애니메이션\n예: 빨간 원이 화면 중앙에서 바운스하는 애니메이션\n예: 5부터 1까지 카운트다운\n예: 3개 텍스트가 순서대로 나타나는 인트로\n예: 타이핑 효과로 한 글자씩 나타나는 텍스트\n예: 프로그레스 바가 0%에서 100%까지 채워지는 로딩 애니메이션';
  };

  const getDescription = () => {
    if (activeTab === 'generate') return t('Editor.ai-scene.description');
    if (activeTab === 'modify') return t('Editor.ai-scene.modify-description');
    return 'AI가 Remotion 코드를 생성합니다. 코드로만 가능한 자유로운 애니메이션을 만들어보세요.';
  };

  return (
    <div className="flex h-full flex-col">
      {/* Tab Bar */}
      <div className="flex border-b border-sys-style-neutral-default">
        <TabButton
          active={activeTab === 'code'}
          onClick={() => {
            setActiveTab('code');
            setError('');
            setChatResponse('');
            // 코드 탭 진입 시 Babel 사전 로드
            preloadBabel();
          }}
          label="리모션 스킬로 생성"
        />
        <TabButton
          active={activeTab === 'modify'}
          onClick={() => {
            setActiveTab('modify');
            setError('');
            setChatResponse('');
          }}
          label="프롬프트로 수정"
        />
        <TabButton
          active={activeTab === 'generate'}
          onClick={() => {
            setActiveTab('generate');
            setError('');
            setChatResponse('');
          }}
          label="프롬프트로 생성"
        />
      </div>

      <div className="flex flex-1 flex-col overflow-y-auto p-md layout-padding-bottom">
        {/* Description */}
        <p className="mb-md text-body-small text-sys-cont-neutral-default">
          {getDescription()}
        </p>

        {/* Item count badge (modify tab) */}
        {activeTab === 'modify' && (
          <div className="mb-sm">
            <span className="rounded-full bg-sys-style-neutral-lightest px-sm py-3xs text-comp-accent-xsmall text-sys-cont-neutral-default">
              {itemCount}개 아이템 감지됨
            </span>
          </div>
        )}

        {/* Reference Image URL (code tab only) */}
        {activeTab === 'code' && (
          <div className="mb-sm flex flex-col gap-3xs">
            <label className="text-comp-accent-xsmall text-sys-cont-neutral-default">
              참조 이미지 URL (선택)
            </label>
            <input
              type="text"
              value={referenceImageUrl}
              onChange={(e) => setReferenceImageUrl(e.target.value)}
              placeholder="https://example.com/image.png"
              disabled={isPending}
              className="text-sys-cont-default w-full rounded-sm border border-sys-style-neutral-default bg-sys-bg-surface px-sm py-xs text-body-small placeholder:text-sys-cont-neutral-light focus:border-sys-cont-primary-default focus:outline-none disabled:opacity-50"
            />
            {referenceImageUrl.trim() && (
              <ImagePreview url={referenceImageUrl} />
            )}
          </div>
        )}

        {/* Prompt Input */}
        <div className="flex flex-col gap-sm">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={getPlaceholder()}
            disabled={isPending}
            rows={10}
            className="text-sys-cont-default w-full resize-none rounded-sm border border-sys-style-neutral-default bg-sys-bg-surface px-sm py-xs text-body-small placeholder:text-sys-cont-neutral-light focus:border-sys-cont-primary-default focus:outline-none disabled:opacity-50"
          />

          <Button
            onClick={getActionHandler()}
            disabled={isActionDisabled}
            className="w-full"
          >
            {isPending ? (
              <span className="flex items-center gap-2xs">
                <LoadingSpinner className="size-4" />
                {getButtonLabel()}
              </span>
            ) : (
              getButtonLabel()
            )}
          </Button>
        </div>

        {/* Chat Response */}
        {chatResponse && (
          <div className="mt-md rounded-sm bg-sys-style-neutral-lightest p-sm">
            <p className="text-sys-cont-default text-body-small">
              {chatResponse}
            </p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-md rounded-sm bg-red-50 p-sm">
            <p className="text-body-small text-red-600">{error}</p>
          </div>
        )}

        {/* Tips */}
        <div className="mt-auto pt-md">
          <p className="text-comp-accent-xsmall text-sys-cont-neutral-light">
            {t('Editor.ai-scene.tips')}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * AI 응답의 아이템 액션(update/create/delete)을 EditorState에 적용합니다.
 */
function applyItemActions(
  state: import('@/features/remotion-editor/editor/state/types').EditorState,
  actions: AiItemAction[],
) {
  const deletes = actions.filter((a) => a.action === 'delete');
  const updates = actions.filter((a) => a.action === 'update');
  const creates = actions.filter((a) => a.action === 'create');

  let updated = state;

  // 1. Delete - 아이템 제거 + 트랙에서 제거
  if (deletes.length > 0) {
    const deleteIds = new Set(deletes.map((d) => d.id));
    const newItems = { ...updated.undoableState.items };
    for (const id of deleteIds) {
      delete newItems[id];
    }
    const newTracks = updated.undoableState.tracks
      .map((track) => ({
        ...track,
        items: track.items.filter((id) => !deleteIds.has(id)),
      }))
      .filter((track) => track.items.length > 0); // 빈 트랙 제거

    updated = {
      ...updated,
      undoableState: {
        ...updated.undoableState,
        items: newItems,
        tracks: newTracks,
      },
      selectedItems: updated.selectedItems.filter((id) => !deleteIds.has(id)),
    };
  }

  // 2. Update - 기존 아이템 속성 변경
  if (updates.length > 0) {
    const updateIds = updates.map((u) => u.id);
    const updateMap = new Map(updates.map((u) => [u.id, u]));

    updated = changeItems(updated, updateIds, (item) => {
      const changes = updateMap.get(item.id);
      if (!changes) return item;
      return applyChangesToItem(item, changes);
    });
  }

  // 3. Create - 새 아이템 생성 + 트랙에 추가
  if (creates.length > 0) {
    const newItems = { ...updated.undoableState.items };
    const newTrackItems: string[] = [];

    for (const action of creates) {
      if (
        !action.type ||
        action.fromFrame == null ||
        action.durationInFrames == null
      )
        continue;

      const id = generateRandomId();
      const fps = updated.undoableState.fps || 30;
      const cw = updated.undoableState.compositionWidth;
      const ch = updated.undoableState.compositionHeight;

      if (action.type === 'text') {
        const textItem: import('@/features/remotion-editor/editor/items/text/text-item-type').TextItem =
          {
            id,
            type: 'text',
            from: action.fromFrame,
            durationInFrames: action.durationInFrames,
            top: action.top ?? Math.round(0.4 * ch),
            left: action.left ?? Math.round(0.1 * cw),
            width: action.width ?? Math.round(0.8 * cw),
            height: action.height ?? Math.round(0.2 * ch),
            opacity: action.opacity ?? 1,
            text: action.text ?? '',
            color: action.color ?? '#FFFFFF',
            align: (action.align as 'left' | 'center' | 'right') ?? 'center',
            fontFamily: action.fontFamily ?? 'Pretendard',
            fontStyle: { variant: 'normal', weight: '700' },
            fontSize: action.fontSize ?? 48,
            lineHeight: 1.3,
            letterSpacing: 0,
            resizeOnEdit: false,
            direction: 'ltr',
            strokeWidth: 0,
            strokeColor: '#000000',
            fadeInDurationInSeconds: action.fadeInSeconds ?? 0.3,
            fadeOutDurationInSeconds: action.fadeOutSeconds ?? 0.3,
            isDraggingInTimeline: false,
            rotation: 0,
            animations: action.animationType
              ? [
                  {
                    type: action.animationType as AnimationType,
                    durationInSeconds: action.fadeInSeconds ?? 0.5,
                  },
                ]
              : undefined,
          };
        newItems[id] = textItem;
        newTrackItems.push(id);
      } else if (action.type === 'solid') {
        const solidItem: import('@/features/remotion-editor/editor/items/solid/solid-item-type').SolidItem =
          {
            id,
            type: 'solid',
            from: action.fromFrame,
            durationInFrames: action.durationInFrames,
            top: 0,
            left: 0,
            width: cw,
            height: ch,
            opacity: action.opacity ?? 1,
            color: action.color ?? '#1A1A2E',
            keepAspectRatio: false,
            fadeInDurationInSeconds: 0,
            fadeOutDurationInSeconds: 0,
            isDraggingInTimeline: false,
            borderRadius: 0,
            rotation: 0,
          };
        newItems[id] = solidItem;
        newTrackItems.push(id);
      }
    }

    if (newTrackItems.length > 0) {
      const newTrack: import('@/features/remotion-editor/editor/state/types').TrackType =
        {
          id: generateRandomId(),
          items: newTrackItems,
          hidden: false,
          muted: false,
          locked: false,
          magnetic: false,
          zone: getZoneForItemType('text'),
        };

      updated = {
        ...updated,
        undoableState: {
          ...updated.undoableState,
          items: newItems,
          tracks: [newTrack, ...updated.undoableState.tracks],
        },
      };
    }
  }

  return updated;
}

/** 단일 아이템에 변경 속성을 적용 */
function applyChangesToItem(
  item: import('@/features/remotion-editor/editor/items/item-type').EditorStarterItem,
  changes: AiItemAction,
) {
  const u = { ...item } as Record<string, unknown>;

  if (changes.top != null) u.top = changes.top;
  if (changes.left != null) u.left = changes.left;
  if (changes.width != null) u.width = changes.width;
  if (changes.height != null) u.height = changes.height;
  if (changes.opacity != null) u.opacity = changes.opacity;
  if (changes.text != null) u.text = changes.text;
  if (changes.fontSize != null) u.fontSize = changes.fontSize;
  if (changes.color != null) u.color = changes.color;
  if (changes.align != null) u.align = changes.align;
  if (changes.fontFamily != null) u.fontFamily = changes.fontFamily;
  if (changes.highlightColor != null) u.highlightColor = changes.highlightColor;
  // Phase 2: 확장 텍스트 스타일
  if (changes.fontStyle != null) u.fontStyle = changes.fontStyle;
  if (changes.lineHeight != null) u.lineHeight = changes.lineHeight;
  if (changes.letterSpacing != null) u.letterSpacing = changes.letterSpacing;
  if (changes.strokeWidth != null) u.strokeWidth = changes.strokeWidth;
  if (changes.strokeColor != null) u.strokeColor = changes.strokeColor;
  if (changes.direction != null) u.direction = changes.direction;
  if (changes.maxLines != null) u.maxLines = changes.maxLines;
  if (changes.shadow !== undefined) u.shadow = changes.shadow;
  if (changes.background !== undefined) u.background = changes.background;
  // Phase 2: 위치/레이아웃 확장
  if (changes.rotation != null) u.rotation = changes.rotation;
  if (changes.borderRadius != null) u.borderRadius = changes.borderRadius;
  if (changes.cropLeft != null) u.cropLeft = changes.cropLeft;
  if (changes.cropTop != null) u.cropTop = changes.cropTop;
  if (changes.cropRight != null) u.cropRight = changes.cropRight;
  if (changes.cropBottom != null) u.cropBottom = changes.cropBottom;
  // video / audio / gif
  if (changes.playbackRate != null) u.playbackRate = changes.playbackRate;
  if (changes.decibelAdjustment != null)
    u.decibelAdjustment = changes.decibelAdjustment;
  if (changes.blurAmount != null) u.blurAmount = changes.blurAmount;
  if (changes.fadeInSeconds != null)
    u.fadeInDurationInSeconds = changes.fadeInSeconds;
  if (changes.fadeOutSeconds != null)
    u.fadeOutDurationInSeconds = changes.fadeOutSeconds;
  // Phase 2: 오디오 페이드 (audioFadeInSeconds → audioFadeInDurationInSeconds)
  if (changes.audioFadeInSeconds != null)
    u.audioFadeInDurationInSeconds = changes.audioFadeInSeconds;
  if (changes.audioFadeOutSeconds != null)
    u.audioFadeOutDurationInSeconds = changes.audioFadeOutSeconds;

  if (changes.animationType !== undefined) {
    if (changes.animationType) {
      u.animations = [
        {
          type: changes.animationType as AnimationType,
          durationInSeconds: changes.fadeInSeconds ?? 0.5,
        },
      ];
    } else {
      u.animations = undefined;
    }
  }

  return u as typeof item;
}

/**
 * AI 응답의 트랜지션 변경사항을 EditorState에 적용합니다.
 * add: 두 인접 클립 사이에 새 트랜지션 생성
 * update: 기존 트랜지션의 타입/방향 변경
 * delete: 기존 트랜지션 삭제
 */
function applyTransitionChanges(
  state: import('@/features/remotion-editor/editor/state/types').EditorState,
  changes: AiTransitionChange[],
  tracks: import('@/features/remotion-editor/editor/state/types').TrackType[],
) {
  const newTransitions = { ...state.undoableState.transitions };

  for (const change of changes) {
    if (change.action === 'delete' && change.transitionId) {
      delete newTransitions[change.transitionId];
      continue;
    }

    if (change.action === 'update' && change.transitionId) {
      const existing = newTransitions[change.transitionId];
      if (existing) {
        newTransitions[change.transitionId] = {
          ...existing,
          transitionType:
            (change.transitionType as TransitionType) ??
            existing.transitionType,
          direction:
            change.direction !== undefined
              ? ((change.direction as TransitionDirection) ?? undefined)
              : existing.direction,
        };
      }
      continue;
    }

    if (change.action === 'add' && change.transitionType) {
      const sourceItem = state.undoableState.items[change.sourceItemId];
      const targetItem = state.undoableState.items[change.targetItemId];
      if (!sourceItem || !targetItem) continue;

      const trackId = tracks.find(
        (t) =>
          t.items.includes(change.sourceItemId) &&
          t.items.includes(change.targetItemId),
      )?.id;
      if (!trackId) continue;

      const edgeFrame = sourceItem.from + sourceItem.durationInFrames;
      const duration = DEFAULT_TRANSITION_DURATION_IN_FRAMES;
      const from = edgeFrame - Math.floor(duration / 2);

      const newId = uuidv4();
      newTransitions[newId] = createTransitionItem({
        id: newId,
        transitionType: change.transitionType as TransitionType,
        direction: (change.direction as TransitionDirection) ?? undefined,
        durationInFrames: duration,
        sourceItemId: change.sourceItemId,
        targetItemId: change.targetItemId,
        trackId,
        from,
      });
    }
  }

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      transitions: newTransitions,
    },
  };
}

/**
 * AI 응답의 효과음 변경사항을 EditorState에 적용합니다.
 * add: 사전 정의된 효과음을 타임라인에 추가
 * delete: 기존 효과음 아이템 삭제
 */
function applySoundEffectChanges(
  state: import('@/features/remotion-editor/editor/state/types').EditorState,
  changes: AiSoundEffectAction[],
) {
  const newItems = { ...state.undoableState.items };
  let newTracks = [...state.undoableState.tracks];
  const newAssets = { ...state.undoableState.assets };
  const fps = state.undoableState.fps || 30;

  for (const change of changes) {
    if (change.action === 'delete' && change.itemId) {
      const deletedItem = newItems[change.itemId];
      delete newItems[change.itemId];
      newTracks = newTracks
        .map((track) => ({
          ...track,
          items: track.items.filter((id: string) => id !== change.itemId),
        }))
        .filter((track) => track.items.length > 0);
      if (deletedItem && 'assetId' in deletedItem && deletedItem.assetId) {
        delete newAssets[deletedItem.assetId as string];
      }
    } else if (change.action === 'add' && change.soundEffectId) {
      const sfDef = getSoundEffectById(change.soundEffectId);
      if (!sfDef) continue;

      const sfItemId = `sfx-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const sfAssetId = `sfx-asset-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const durationInFrames = Math.round((sfDef.duration / 1000) * fps);

      newAssets[sfAssetId] = {
        id: sfAssetId,
        type: 'audio',
        filename: `${sfDef.name}.mp3`,
        size: 0,
        mimeType: 'audio/mpeg',
        remoteUrl: null,
        remoteFileKey: null,
        staticPath: sfDef.path,
        durationInSeconds: sfDef.duration / 1000,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;

      newItems[sfItemId] = {
        id: sfItemId,
        type: 'audio',
        assetId: sfAssetId,
        from: change.fromFrame ?? 0,
        durationInFrames,
        top: 0,
        left: 0,
        width: state.undoableState.compositionWidth,
        height: 0,
        opacity: 1,
        audioStartFromInSeconds: 0,
        decibelAdjustment: 0,
        playbackRate: 1,
        audioFadeInDurationInSeconds: 0,
        audioFadeOutDurationInSeconds: 0,
        isDraggingInTimeline: false,
        renderMeta: { clipType: 'audio' },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any;

      const sfTrackId = `track-sfx-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      newTracks = [
        {
          id: sfTrackId,
          items: [sfItemId],
          hidden: false,
          muted: false,
          locked: false,
          magnetic: false,
          zone: getZoneForItemType('audio'),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any,
        ...newTracks,
      ];
    }
  }

  return {
    ...state,
    undoableState: {
      ...state.undoableState,
      items: newItems,
      tracks: newTracks,
      assets: newAssets,
    },
  };
}

function ImagePreview({ url }: { url: string }) {
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>(
    'loading',
  );

  // URL 변경 시 상태 리셋
  useEffect(() => {
    setStatus('loading');
  }, [url]);

  return (
    <div className="flex flex-col gap-3xs">
      <span className="text-comp-accent-xsmall text-sys-cont-neutral-light">
        이미지 미리보기
      </span>
      <div className="relative h-40 w-full overflow-hidden rounded-sm border border-sys-style-neutral-default bg-sys-bg-surface">
        {status === 'error' && (
          <div className="flex h-full items-center justify-center">
            <span className="text-comp-accent-xsmall text-sys-cont-neutral-light">
              이미지를 불러올 수 없습니다
            </span>
          </div>
        )}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={url}
          alt="참조 이미지"
          referrerPolicy="no-referrer"
          className={`h-full w-full object-contain ${status === 'error' ? 'hidden' : ''}`}
          onLoad={() => setStatus('loaded')}
          onError={() => setStatus('error')}
        />
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 px-md py-xs text-comp-accent-small transition-colors ${
        active
          ? 'border-b-2 border-sys-cont-primary-default text-sys-cont-primary-default'
          : 'hover:text-sys-cont-default text-sys-cont-neutral-default'
      }`}
    >
      {label}
    </button>
  );
}

export default AiSceneContent;
